// src/constants/i18n.factory.ts
// ============================================================================
// i18n Factory — ONE implementation, works for he/en as long as locale shape matches
// ============================================================================

import type { AlbumName, SectionFourSEO, SeoService } from '@/constants/core';
import {
  ALBUM,
  BASE_URL,
  BUSINESS_BASE,
  EVENT_GROUP,
  preferredMapsUrl,
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_WEDDING,
  withBet,
} from '@/constants/core';
import { isRegionName, type Region, type SeoCity } from '@/constants/geo';

// IMPORTANT: we type by `he` shape, and require `en` to match it.
// 👇 Import VALUE to derive type safely
import { he } from '@/i18n/locales/he';
import { en } from '@/i18n/locales/en';
import { translateLocationLabel } from './i18n.en';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

// Use Hebrew locale as canonical structure
type LocaleShape = {
    meta: {
      lang: string;
      langFull: string;
      dir: 'rtl' | 'ltr';
      name: string;
    };
  
    words: any;
    brand: any;
    business: any;
  
    home: any;
    footer: any;
  
    section1: any;
    section2: any;
    section3: any;
    section4: any;
    section5: any;
    section6: any;
  
    metaDescription: any;
    menu:any;
    seo:any
    geo:any
  };


function normalizeWhitespace(s: string) {
  return String(s ?? '').replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').replace(/\s+/g, ' ').trim();
}

/**
 * Locale-aware location prefix:
 * - he: "ב${city}"
 * - en: "in ${city}"
 */
function makeCityPrefix(locale: LocaleShape, cityOrLabel: string) {
  const name = String(cityOrLabel ?? '').trim();
  if (!name) return '';

  return locale.meta.lang === 'he'
    ? `ב${name}`         // ✅ הוספת "ב"
    : `in ${name}`;
}

/**
 * Same as above but returns the prefix *including leading space when needed*
 * so you can safely do: `${keyword}${cityBet}`
 */
function makeCitySuffix(locale: LocaleShape, cityOrLabel: string) {
  const pref = makeCityPrefix(locale, cityOrLabel);
  if (!pref) return '';

  return ` ${pref}`;     // תמיד עם רווח
}



export type AppLang = 'he' | 'en';

export type I18nConstants = ReturnType<typeof createI18nConstants>;

export function createI18nConstants(locale: LocaleShape) {

  function translateKeywordForLocale(keyword: string) {
    const k = String(keyword ?? '').trim();
  
    if (!k) return '';
  
    // אם באנגלית — החזר DJ
    if (locale.meta.lang === 'en') {
      const lower = k.toLowerCase();
  
      if (
        lower.includes('dj') ||
        lower.includes('די') ||
        lower.includes('ג׳יי')
      ) {
        return WORDS.dj.en; // "DJ"
      }
    }
  
    return k;
  }
  function cityBetForLocale(cityOrLabel: SeoCity | string) {
    const raw = String(cityOrLabel ?? '').trim();
    if (!raw) return '';
  
    const translated = translateLocationLabel(raw);
  
    // Uses the locale of THIS factory instance
    return makeCitySuffix(locale, translated);
  }
  // ─────────────────────────────────────────────────────────────
  // Brand / Words
  // ─────────────────────────────────────────────────────────────
  const WORDS = locale.words;
  const FIRST_NAME = locale.brand.firstName;
  const BRAND_TITLE_SHORT = locale.brand.short;
  const BRAND_TITLE_HE = locale.brand.title;

  // ─────────────────────────────────────────────────────────────
  // BUSINESS
  // ─────────────────────────────────────────────────────────────
  const BUSINESS = {
    name: locale.business.name,

    phone: BUSINESS_BASE.phone,
    email: BUSINESS_BASE.email,

    address: {
      street: locale.business.address.street,
      city: locale.business.address.city,
      postal: locale.business.address.postal,
      country: locale.business.address.country,

      lat: BUSINESS_BASE.address.lat,
      lng: BUSINESS_BASE.address.lng,
      mapUrl: BUSINESS_BASE.address.mapUrl,
    },

    googlePlaceId: BUSINESS_BASE.googlePlaceId,
    sameAs: BUSINESS_BASE.sameAs,
  } as const;

  // ─────────────────────────────────────────────────────────────
  // HTML / Home / Footer
  // ─────────────────────────────────────────────────────────────
  const HTML_LANG = locale.meta.lang;
  const HTML_LANG_FULL = locale.meta.langFull;

  const HOME_TITLE = locale.home.title;
  const HOME_DESC = locale.home.description;
  const HOME_HERO_TITLE = locale.home.heroTitle;

  const FOOTER_TEXT = locale.footer.copyright(new Date().getFullYear());

  // ─────────────────────────────────────────────────────────────
  // Section 2
  // ─────────────────────────────────────────────────────────────
  const SECTION_TWO_TITLE = locale.section2.title;
  const SECTION_TWO_DESCRIPTION = locale.section2.description;
  const SECTION_TWO_ADDITIONAL_TEXT = locale.section2.additionalText;
  const SECTION_TWO_IMG_ALT = locale.section2.imageAltPortrait;
  const SECTION_TWO_HORIZONTAL_IMG_ALT = locale.section2.imageAltHorizontal;

  // ─────────────────────────────────────────────────────────────
  // Section 3
  // ─────────────────────────────────────────────────────────────
  const SECTION_THREE_TITLE = locale.section3.title;
  const SECTION_THREE_FALLBACK_TEXT = locale.section3.fallbackText;
  const SECTION_THREE_ALBUMS_ERROR = locale.section3.albumsError;
  const SECTION_THREE_FILES_ERROR = locale.section3.filesError;
  const SECTION_THREE_LOADING_TEXT = locale.section3.loading;

  const albumDescriptions: Record<AlbumName, string> = {
    חתונות: locale.section3.albumDescriptions.weddings,
    חינות: locale.section3.albumDescriptions.hennas,
    'בר/בת מצוות': locale.section3.albumDescriptions.barBat,
    פורום: locale.section3.albumDescriptions.forum,
    כתבות: locale.section3.albumDescriptions.articles,
    המלצות: locale.section3.albumDescriptions.recommendations,
    ברים: locale.section3.albumDescriptions.bars,
    פלאיירים: locale.section3.albumDescriptions.flyers,
  };

  const albumsText: Record<AlbumName, string> = {
    חתונות: locale.section3.albumTexts.weddings,
    חינות: locale.section3.albumTexts.hennas,
    'בר/בת מצוות': locale.section3.albumTexts.barBat,
    פורום: locale.section3.albumTexts.forum,
    כתבות: locale.section3.albumTexts.articles,
    המלצות: locale.section3.albumTexts.recommendations,
    ברים: locale.section3.albumTexts.bars,
    פלאיירים: locale.section3.albumTexts.flyers,
  };

  // ─────────────────────────────────────────────────────────────
  // Section 4
  // ─────────────────────────────────────────────────────────────
  const SECTION_FOUR_TITLE = locale.section4.title;
  const SECTION_FOUR_DESCRIPTION = locale.section4.description;

  const SECTION_FOUR_CONTACT_GROUP_TITLE = locale.section4.groupTitles.contact;
  const SECTION_FOUR_EVENTS_GROUP_TITLE = locale.section4.groupTitles.events;
  const SECTION_FOUR_SERVICES_GROUP_TITLE = locale.section4.groupTitles.services;

  // Contact buttons
  const telHref = `tel:${BUSINESS.phone.replace(/[^+\d]/g, '')}`;

  const ig = BUSINESS.sameAs.find((u: any) => u.includes('instagram.com'));
  const fb = BUSINESS.sameAs.find((u: any) => u.includes('facebook.com'));

  const SECTION_FOUR_CONTACT_BUTTONS = {
    phone: { label: locale.section4.contactButtons.phone, href: telHref },
    whatsapp: {
      label: locale.section4.contactButtons.whatsapp,
      href: 'https://wa.me/972528623221',
    },
    email: {
      label: locale.section4.contactButtons.email,
      href: `mailto:${BUSINESS.email}`,
    },
    instagram: {
      label: locale.section4.contactButtons.instagram,
      href: ig ?? BASE_URL,
    },
    facebook: {
      label: locale.section4.contactButtons.facebook,
      href: fb ?? BASE_URL,
    },
    navigate: {
      label: locale.section4.contactButtons.navigate,
      href: preferredMapsUrl(),
    },
  } as const;

  // Event buttons
  const SECTION_FOUR_EVENT_BUTTONS = [
    {
      label: WORDS.events.wedding.many,
      icon: 'ring',
      prefill: locale.section4.prefills.wedding,
    },
    {
      label: WORDS.events.henna.many,
      icon: 'vest',
      prefill: locale.section4.prefills.henna,
    },
    {
      label: WORDS.events.bar_bat.many,
      icon: 'synagogue',
      prefill: locale.section4.prefills.barBat,
    },
    {
      label: WORDS.events.generic.many,
      icon: 'glass',
      prefill: locale.section4.prefills.generic,
    },
  ] as const;

  // Service buttons
  const SECTION_FOUR_SERVICE_BUTTONS = [
    { label: locale.section4.services.sound.label, icon: 'volume', prefill: '' },
    { label: locale.section4.services.light.label, icon: 'light', prefill: '' },
    { label: locale.section4.services.attractions.label, icon: 'star', prefill: '' },
    { label: locale.section4.services.recording.label, icon: 'mic', prefill: '' },
  ] as const;

  // ─────────────────────────────────────────────────────────────
  // Section 5
  // ─────────────────────────────────────────────────────────────
  const SECTION_FIVE_TITLE = locale.section5.title;
  const SECTION_FIVE_LINKS_HEADING = locale.section5.linksHeading;

  // ─────────────────────────────────────────────────────────────
  // Meta description
  // ─────────────────────────────────────────────────────────────
  function makeMetaDescription(service: string, location?: string) {
    const loc = location?.trim();
    const locText = loc ? makeCitySuffix(locale, translateLocationLabel(loc)) : '';
  
    const map = locale.metaDescription;
  
    if (service === 'חתונה') return `${map.wedding}${locText}`;
    if (service === 'חינה') return `${map.henna}${locText}`;
    if (service === 'בר-מצווה') return `${map.barBat}${locText}`;
  
    return `${map.generic}${locText}`;
  }

  // ─────────────────────────────────────────────────────────────
  // Brand regex
  // ─────────────────────────────────────────────────────────────
  const BRAND_PREFIX_RE = new RegExp(
    `^די ג['\\u05F3]יי\\s+${BRAND_TITLE_SHORT}\\s*\\|\\s*`,
    'u'
  );

  const BRAND_SUFFIX_RE = new RegExp(
    `\\s*\\|\\s*די ג['\\u05F3]יי\\s+${BRAND_TITLE_SHORT}\\s*$`,
    'u'
  );

  // ─────────────────────────────────────────────────────────────
  // SEO keyword variants
  // ─────────────────────────────────────────────────────────────
  const SEO_KEYWORD_VARIANTS = {
    חתונה: [
      `${WORDS.mc} ${WORDS.events.wedding.to}`,
      `${WORDS.dj.he} ${WORDS.events.wedding.to}`,
      `${WORDS.dj.en} ${WORDS.events.wedding.to}`,
    ],
    חינה: [
      `${WORDS.mc} ${WORDS.events.henna.to}`,
      `${WORDS.dj.he} ${WORDS.events.henna.to}`,
      `${WORDS.dj.en} ${WORDS.events.henna.to}`,
    ],
    'בר-מצווה': [
      `${WORDS.mc} ${WORDS.events.bar_bat.to}`,
      `${WORDS.dj.he} ${WORDS.events.bar_bat.to}`,
      `${WORDS.dj.en} ${WORDS.events.bar_bat.to}`,
    ],
    אירוע: [
      `${WORDS.mc} ${WORDS.events.generic.to}`,
      `${WORDS.dj.he} ${WORDS.events.generic.to}`,
      `${WORDS.dj.en} ${WORDS.events.generic.to}`,
    ],
  } as const;

  function preferredKeyword(service: string) {
    const arr = SEO_KEYWORD_VARIANTS[service as keyof typeof SEO_KEYWORD_VARIANTS];
    return arr?.[0] ?? service;
  }

  function makeTitle(keyword: string, city?: string) {
    const loc = city?.trim();
    return loc ? `${keyword} ב${loc} | ${BRAND_TITLE_HE}` : `${keyword} | ${BRAND_TITLE_HE}`;
  }

  const preferredKeywordForService = (service: SeoService): string => {
    const arr = SEO_KEYWORD_VARIANTS[service as keyof typeof SEO_KEYWORD_VARIANTS] as readonly string[];
    return arr.find((v) => v.includes(WORDS.mc)) ?? arr[0] ?? service;
  };

  function buildWhatsAppWithText(raw: string): string {
    const normalized = raw.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    const encoded = encodeURIComponent(normalized);
    const base = SECTION_FOUR_CONTACT_BUTTONS.whatsapp.href;
    return `${base}?text=${encoded}`;
  }

  function citySuffix(cityOrLabel: SeoCity | string) {
    // ⬅️ אם אתה רוצה באנגלית גם תרגום עיר (Tel Aviv, Ashdod...)
    const translated = translateLocationLabel(String(cityOrLabel));
    return makeCitySuffix(locale, translated);
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers (internal)
  // ─────────────────────────────────────────────────────────────
  const isGenericDjKeyword = (keyword?: string) =>
    !keyword ||
    keyword.trim() === '' ||
    keyword.trim() === WORDS.dj.he_alt ||
    keyword.trim() === WORDS.dj.he ||
    keyword.trim().toLowerCase() === WORDS.dj.en.toLowerCase();

  // ─────────────────────────────────────────────────────────────
  // EVENT TYPE DESCRIPTION (mobile / desktop)
  // ─────────────────────────────────────────────────────────────
  const SECTION_THREE_DEFAULT_ALBUM_BY_SERVICE: Record<SeoService, AlbumName> = {
    [SEO_SERVICE_WEDDING]: ALBUM.weddings,
    [SEO_SERVICE_HENNA]: ALBUM.hennas,
    [SEO_SERVICE_BAR_BAT]: ALBUM.barBat,
    [SEO_SERVICE_EVENT]: ALBUM.weddings,
  } as const;

  type EventTypeDescriptionVariant = {
    mobile: (city: SeoCity) => string;
    desktop: (city: SeoCity) => string;
  };

  const EVENT_TYPE_DESCRIPTION_COPY: Record<SeoService, EventTypeDescriptionVariant> = {
    [SEO_SERVICE_WEDDING]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.wedding.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.wedding.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  
    [SEO_SERVICE_EVENT]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.event.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.event.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  
    [SEO_SERVICE_BAR_BAT]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.bar_bat.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.bar_bat.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  
    [SEO_SERVICE_HENNA]: {
      mobile: (city) =>
        locale.section5.eventTypeDescription.henna.mobile(
          translateCity(city),
          citySuffix(city)
        ),
      desktop: (city) =>
        locale.section5.eventTypeDescription.henna.desktop(
          translateCity(city),
          citySuffix(city)
        ),
    },
  };

  // ─────────────────────────────────────────────────────────────
  // GALLERY HEADINGS (Section 3)
  // ─────────────────────────────────────────────────────────────
  const GALLERY_HEADINGS_COPY: Record<SeoService, (keyword: string, city: SeoCity) => string[]> = {
    [SEO_SERVICE_WEDDING]: (keyword, city) => locale.section3.galleryHeadings.wedding(keyword, withBet(city)),
    [SEO_SERVICE_HENNA]: (keyword, city) => locale.section3.galleryHeadings.henna(keyword, withBet(city)),
    [SEO_SERVICE_BAR_BAT]: (keyword, city) => locale.section3.galleryHeadings.bar_bat(keyword, withBet(city)),
    [SEO_SERVICE_EVENT]: (keyword, city) => locale.section3.galleryHeadings.event(keyword, withBet(city)),
  };

  // ─────────────────────────────────────────────────────────────
  // Section 4 SEO copy (intros + events + services + contact)
  // ─────────────────────────────────────────────────────────────
  type Section4SeoTemplate = (keyword: string, cityBet: string) => string;

  const SECTION4_EVENTS_INTRO_COPY: Record<SeoService, Section4SeoTemplate> = {
    [SEO_SERVICE_WEDDING]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.wedding(keyword, cityBet),

    [SEO_SERVICE_HENNA]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.henna(keyword, cityBet),

    [SEO_SERVICE_BAR_BAT]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.bar_bat(keyword, cityBet),

    [SEO_SERVICE_EVENT]: (keyword, cityBet) =>
      isGenericDjKeyword(keyword) ? locale.section4.eventsIntro.default : locale.section4.eventsIntro.event(keyword, cityBet),
  };

  const SECTION4_SERVICES_INTRO_COPY: Record<SeoService, Section4SeoTemplate> = {
    [SEO_SERVICE_WEDDING]: locale.section4.servicesIntro.wedding,
    [SEO_SERVICE_HENNA]: locale.section4.servicesIntro.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section4.servicesIntro.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section4.servicesIntro.event,
  };

  const SECTION4_CONTACT_COPY: SectionFourSEO['contact'] = locale.section4.contactSeo;

  const SECTION4_EVENTS_TEXTS_TEMPLATE = (cityBet: string): SectionFourSEO['events'] => ({
    [EVENT_GROUP.weddings]: locale.section4.eventsTexts.wedding(cityBet),
    [EVENT_GROUP.hennas]: locale.section4.eventsTexts.henna(cityBet),
    [EVENT_GROUP.barBat]: locale.section4.eventsTexts.bar_bat(cityBet),
    [EVENT_GROUP.events]: locale.section4.eventsTexts.event(cityBet),
  });

  const SECTION4_SERVICES_TEXTS_TEMPLATE: SectionFourSEO['services'] = {
    sound: locale.section4.services.sound.description,
    light: locale.section4.services.light.description,
    attractions: locale.section4.services.attractions.description,
    recording: locale.section4.services.recording.description,
  };

  function buildSection4SeoCopy(
    service: SeoService,
    keyword: string,
    city: SeoCity
  ): SectionFourSEO {
  
    const cityBet = citySuffix(city); // ⭐️ במקום withBet
  
    const eventsIntro = SECTION4_EVENTS_INTRO_COPY[service](
      keyword,
      cityBet
    );
  
    const servicesIntro = SECTION4_SERVICES_INTRO_COPY[service](
      keyword,
      cityBet
    );
  
    return {
      intros: { events: eventsIntro, services: servicesIntro },
      contact: SECTION4_CONTACT_COPY,
      events: SECTION4_EVENTS_TEXTS_TEMPLATE(cityBet),
      services: SECTION4_SERVICES_TEXTS_TEMPLATE,
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Section 4 group titles
  // ─────────────────────────────────────────────────────────────
  const SECTION4_GROUP_TITLES_COPY: Record<
  SeoService,
  (keyword: string, city: SeoCity) => {
    contact: string;
    events: string;
    services: string;
    links: string;
  }
> = {
  [SEO_SERVICE_WEDDING]: (keyword, city) =>
    locale.section4.groupTitlesSeo.wedding(
      keyword,
      cityBetForLocale(city),
      BRAND_TITLE_HE
    ),

  [SEO_SERVICE_HENNA]: (keyword, city) =>
    locale.section4.groupTitlesSeo.henna(
      keyword,
      cityBetForLocale(city),
      BRAND_TITLE_HE
    ),

  [SEO_SERVICE_BAR_BAT]: (keyword, city) =>
    locale.section4.groupTitlesSeo.bar_bat(
      keyword,
      cityBetForLocale(city),
      BRAND_TITLE_HE
    ),

  [SEO_SERVICE_EVENT]: (keyword, city) =>
    locale.section4.groupTitlesSeo.event(
      translateKeywordForLocale(keyword),
      cityBetForLocale(city),
      BRAND_TITLE_HE
    )
};

  const SECTION5_HEADINGS_COPY: Record<SeoService, (keyword: string, city: SeoCity, hasCity: boolean) => string[]> = {
    [SEO_SERVICE_WEDDING]: (keyword, city) => locale.section5.headingsSeo.wedding(keyword, withBet(city)),
    [SEO_SERVICE_HENNA]: (keyword, city) => locale.section5.headingsSeo.henna(keyword, withBet(city)),
    [SEO_SERVICE_BAR_BAT]: (keyword, city) => locale.section5.headingsSeo.bar_bat(keyword, withBet(city)),
    [SEO_SERVICE_EVENT]: (keyword, city, hasCity) => locale.section5.headingsSeo.event(keyword, withBet(city), hasCity),
  };

  // ─────────────────────────────────────────────────────────────
  // EXTRA COPY — factory helpers (Section 1/2/4/6 + hubs + home)
  // ─────────────────────────────────────────────────────────────
  const SECTION1_TARGET_DESCRIPTION_LINES_BASE = locale.section1.targetPageLines.base;

  const SECTION1_TARGET_DESCRIPTION_LINES_BY_SERVICE: Record<SeoService, readonly string[]> = {
    [SEO_SERVICE_WEDDING]: locale.section1.targetPageLines.wedding,
    [SEO_SERVICE_HENNA]: locale.section1.targetPageLines.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section1.targetPageLines.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section1.targetPageLines.event,
  };

  const buildSection1TargetSignatureLine = (keyword: string, cityOrLabel: string) => {
    const suffix = makeCitySuffix(locale, cityOrLabel);
    return `${BRAND_TITLE_SHORT} - ${keyword}${suffix}`.trim();
  };
  function buildSection1TargetDescriptionLines(service: SeoService, keyword: string, cityOrLabel: SeoCity | string): string[] {
    const base = SECTION1_TARGET_DESCRIPTION_LINES_BY_SERVICE[service] ?? SECTION1_TARGET_DESCRIPTION_LINES_BASE;
    return [...base, buildSection1TargetSignatureLine(keyword, String(cityOrLabel))];
  }

  const SECTION2_TARGET_ADDITIONAL_BY_SERVICE: Record<SeoService, string> = {
    [SEO_SERVICE_WEDDING]: locale.section2.targetAdditionalByService.wedding,
    [SEO_SERVICE_HENNA]: locale.section2.targetAdditionalByService.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section2.targetAdditionalByService.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section2.targetAdditionalByService.event,
  };

  const buildSection2TargetDescription = (baseDescription: string, djKeyword: string, cityOrLabel: string) =>
    locale.section2.targetDescription(baseDescription, djKeyword, makeCitySuffix(locale, cityOrLabel));

  const SECTION2_TARGET_ADDITIONAL_SUFFIX_BY_SERVICE: Record<SeoService, string> = {
    [SEO_SERVICE_WEDDING]: locale.section2.targetAdditionalSuffixByService.wedding,
    [SEO_SERVICE_HENNA]: locale.section2.targetAdditionalSuffixByService.henna,
    [SEO_SERVICE_BAR_BAT]: locale.section2.targetAdditionalSuffixByService.bar_bat,
    [SEO_SERVICE_EVENT]: locale.section2.targetAdditionalSuffixByService.event,
  };

  const buildSection2TargetAdditionalText = (service: SeoService, baseAdditional: string, cityOrLabel: SeoCity | string) => {
    const citySuffix = makeCitySuffix(locale, String(cityOrLabel));

    const base = SECTION2_TARGET_ADDITIONAL_BY_SERVICE[service] ?? baseAdditional;
    const suffix = SECTION2_TARGET_ADDITIONAL_SUFFIX_BY_SERVICE[service] ?? locale.section2.fallbackSuffix;

    // locale function expects "cityBet" param — we now pass suffix like "בX" / " in X"
    return locale.section2.targetAdditionalText(base, citySuffix, suffix);
  };


  function buildSection4BlockCopy(keyword: string, cityName: string) {
    const hasCity = cityName.trim().length > 0;

    if (hasCity) {
      return locale.section4.blockCopy.withCity(keyword, cityName, locale.brand.firstName);
    }

    return locale.section4.blockCopy.withoutCity(keyword, locale.words.events.generic.one, locale.brand.firstName);
  }

  const buildSection6DefaultTitle = (_keyword: string, _cityOrLabel: SeoCity | string) =>
    locale.section6.defaults.title(locale.brand.title);

  const buildSection6DefaultSubtitle = (keyword: string, cityOrLabel: SeoCity | string) =>
    locale.section6.defaults.subtitle(
      locale.brand.short,
      keyword,
      cityBetForLocale(cityOrLabel)
    );
  const buildCityHubSection1DescriptionLines = (keyword: string, city: SeoCity) =>
    locale.section1.hub.city.descriptionLines(keyword, makeCitySuffix(locale, city), locale.brand.title);

  const buildCityHubSection1ExtraHeadings = (keyword: string, city: SeoCity) =>
    locale.section1.hub.city.extraHeadings(keyword, makeCitySuffix(locale, city));

  const buildCityHubSection3ExtraHeadings = (city: SeoCity) => {
    const suffix = makeCitySuffix(locale, city);
    // Keep your original Hebrew copy; if you want EN here too, we can add it later.
    return [`רגעים מהרחבה${suffix}`];
  };
  const buildRegionHubSection1DescriptionLines = (keyword: string, regionLabel: string) =>
    locale.section1.hub.region.descriptionLines(keyword, makeCitySuffix(locale, regionLabel), locale.brand.title);

  const buildRegionHubSection1ExtraHeadings = (keyword: string, regionLabel: string) =>
    locale.section1.hub.region.extraHeadings(keyword, makeCitySuffix(locale, regionLabel));

  const HOME_SECTION1_DESCRIPTION_LINES = locale.home.sections.section1Lines(
    WORDS.events.generic.one,
    WORDS.mc,
    locale.brand.title
  );

  const HOME_SECTION2_DESCRIPTION_SUFFIX = locale.home.sections.section2DescriptionSuffix(WORDS.events.generic.one);
  const HOME_SECTION2_ADDITIONAL_SUFFIX = locale.home.sections.section2AdditionalSuffix;

  const HOME_SECTION5_SUBTITLE_DESKTOP = locale.home.sections.section5SubtitleDesktop(
    WORDS.events.wedding.one,
    WORDS.events.henna.one,
    WORDS.events.bar_bat.one,
    WORDS.events.generic.one
  );

  const HOME_SECTION5_SUBTITLE_MOBILE = locale.home.sections.section5SubtitleMobile(
    WORDS.events.wedding.many,
    WORDS.events.henna.many,
    WORDS.events.bar_bat.one,
    WORDS.events.generic.many,
    WORDS.events.generic.one
  );

  const HOME_SECTION6_TITLE = locale.home.sections.section6Title(locale.brand.title);
  const HOME_SECTION6_SUBTITLE = locale.home.sections.section6Subtitle(WORDS.events.generic.one);

  const MENU_SECTIONS = locale.menu.sections as readonly {
    id: string;
    label: string;
  }[];

  const ALBUM_LABELS: Record<AlbumName, string> = {
    חתונות: WORDS.events.wedding.many,
    חינות: WORDS.events.henna.many,
    'בר/בת מצוות': WORDS.events.bar_bat.many,
    פורום: WORDS.albums.forum,
    כתבות: WORDS.albums.articles,
    המלצות: WORDS.albums.recommendations,
    ברים: WORDS.albums.bars,
    פלאיירים: WORDS.albums.flyers,
  };

  const FAQ_RAW: any = locale.seo?.faq ?? {};

  // Map: SeoService -> canonical FAQ key in locale packs (en keys)
  const FAQ_KEY_BY_SERVICE: Record<SeoService, string> = {
    [SEO_SERVICE_WEDDING]: 'wedding',
    [SEO_SERVICE_HENNA]: 'henna',
    [SEO_SERVICE_BAR_BAT]: 'bar_bat',
    [SEO_SERVICE_EVENT]: 'event',
  } as const;
  
  function getFaqArrayForService(service: SeoService): any[] {
    // 1) Try exact key (in case locale uses Hebrew keys identical to SeoService)
    const direct = FAQ_RAW?.[service];
    if (Array.isArray(direct) && direct.length) return direct;
  
    // 2) Try canonical EN key (wedding/henna/bar_bat/event)
    const k = FAQ_KEY_BY_SERVICE[service];
    const byCanonical = FAQ_RAW?.[k];
    if (Array.isArray(byCanonical) && byCanonical.length) return byCanonical;
  
    // 3) Try common Hebrew keys (if locale uses Hebrew keys but service is different)
    //    (these are safe fallbacks and won't break if not present)
    const altHebKeys =
      service === SEO_SERVICE_WEDDING
        ? ['חתונה']
        : service === SEO_SERVICE_HENNA
          ? ['חינה']
          : service === SEO_SERVICE_BAR_BAT
            ? ['בר-מצווה', 'בר/בת מצווה', 'בר בת מצווה']
            : ['אירוע'];
  
    for (const hk of altHebKeys) {
      const arr = FAQ_RAW?.[hk];
      if (Array.isArray(arr) && arr.length) return arr;
    }
  
    return [];
  }
  
  function buildFaq(service: SeoService, city: SeoCity | '') {
    const raw =
      getFaqArrayForService(service) ||
      FAQ_RAW?.homeEvent || // ⭐ fallback לדף הבית אם אין התאמה
      [];
  
    const cityText = city ? withBet(city) : '';
  
    return (Array.isArray(raw) ? raw : []).map((item: any) => ({
      q: String(item.q ?? '').replace('{city}', cityText),
      a: String(item.a ?? '').replace('{city}', cityText),
    }));
  }

  function translateRegion(region: Region | string): string {
    const r = String(region ?? '').trim();
    if (!r) return '';
    // he -> returns same, en -> map
    return (locale.geo?.regions?.[r as Region] as string) || r;
  }
  
  function translateCity(city: SeoCity | string): string {
    const c = String(city ?? '').trim();
    if (!c) return '';
    // he -> returns same, en -> transliteration/common spelling
    return (locale.geo?.cities?.[c as SeoCity] as string) || c;
  }
  
  /**
   * Used for UI labels (title/description/link labels) when we have a "locationLabel"
   * that can be either a Region or a City (but slug itself remains Hebrew).
   */
  function translateLocationLabel(label: string): string {
    const raw = String(label ?? '').trim();
    if (!raw) return '';
    if (isRegionName(raw)) return translateRegion(raw as Region);
    return translateCity(raw as SeoCity);
  }

  const SECTION6_REVIEWS_COUNT = (count: number) =>
    locale.section6.reviewsCount(count);

    // ─────────────────────────────────────────────────────────────
  // Return SSOT object
  // ─────────────────────────────────────────────────────────────
  return {
    // Brand/Words
    WORDS,
    FIRST_NAME,
    BRAND_TITLE_SHORT,
    BRAND_TITLE_HE,

    // Business
    BUSINESS,

    // HTML/Home/Footer
    HTML_LANG,
    HTML_LANG_FULL,
    HOME_TITLE,
    HOME_DESC,
    HOME_HERO_TITLE,
    FOOTER_TEXT,

    // Section2
    SECTION_TWO_TITLE,
    SECTION_TWO_DESCRIPTION,
    SECTION_TWO_ADDITIONAL_TEXT,
    SECTION_TWO_IMG_ALT,
    SECTION_TWO_HORIZONTAL_IMG_ALT,

    // Section3
    SECTION_THREE_TITLE,
    SECTION_THREE_FALLBACK_TEXT,
    SECTION_THREE_ALBUMS_ERROR,
    SECTION_THREE_FILES_ERROR,
    SECTION_THREE_LOADING_TEXT,
    albumDescriptions,
    albumsText,

    // Section4
    SECTION_FOUR_TITLE,
    SECTION_FOUR_DESCRIPTION,
    SECTION_FOUR_CONTACT_GROUP_TITLE,
    SECTION_FOUR_EVENTS_GROUP_TITLE,
    SECTION_FOUR_SERVICES_GROUP_TITLE,
    SECTION_FOUR_CONTACT_BUTTONS,
    SECTION_FOUR_EVENT_BUTTONS,
    SECTION_FOUR_SERVICE_BUTTONS,

    // Section5
    SECTION_FIVE_TITLE,
    SECTION_FIVE_LINKS_HEADING,

    // Meta/SEO helpers
    makeMetaDescription,
    BRAND_PREFIX_RE,
    BRAND_SUFFIX_RE,
    SEO_KEYWORD_VARIANTS,
    preferredKeyword,
    makeTitle,
    preferredKeywordForService,
    buildWhatsAppWithText,

    // Factories / SEO copy maps
    SECTION_THREE_DEFAULT_ALBUM_BY_SERVICE,
    EVENT_TYPE_DESCRIPTION_COPY,
    GALLERY_HEADINGS_COPY,
    buildSection4SeoCopy,
    SECTION4_GROUP_TITLES_COPY,
    SECTION5_HEADINGS_COPY,

    // Extra copy builders
    SECTION1_TARGET_DESCRIPTION_LINES_BASE,
    SECTION1_TARGET_DESCRIPTION_LINES_BY_SERVICE,
    buildSection1TargetSignatureLine,
    buildSection1TargetDescriptionLines,
    SECTION2_TARGET_ADDITIONAL_BY_SERVICE,
    buildSection2TargetDescription,
    SECTION2_TARGET_ADDITIONAL_SUFFIX_BY_SERVICE,
    buildSection2TargetAdditionalText,
    buildSection4BlockCopy,
    buildSection6DefaultTitle,
    buildSection6DefaultSubtitle,
    buildCityHubSection1DescriptionLines,
    buildCityHubSection1ExtraHeadings,
    buildCityHubSection3ExtraHeadings,
    buildRegionHubSection1DescriptionLines,
    buildRegionHubSection1ExtraHeadings,

    // Home computed
    HOME_SECTION1_DESCRIPTION_LINES,
    HOME_SECTION2_DESCRIPTION_SUFFIX,
    HOME_SECTION2_ADDITIONAL_SUFFIX,
    HOME_SECTION5_SUBTITLE_DESKTOP,
    HOME_SECTION5_SUBTITLE_MOBILE,
    HOME_SECTION6_TITLE,
    HOME_SECTION6_SUBTITLE,

    MENU_SECTIONS,
    ALBUM_LABELS,
    FAQ_BY_SERVICE: FAQ_RAW,
    buildFaq,
    translateCity,
    translateRegion,
    translateLocationLabel,
    footer: {
      recommendedPages: locale.footer.recommendedPages,
      copyright: locale.footer.copyright,
    },
    SECTION6_REVIEWS_COUNT
  } as const;
}

// Optional convenience selector (dynamic)
export function createI18n(lang: AppLang): I18nConstants {
    const locale = lang === 'he' ? he : en;
    return createI18nConstants(locale);
  }


